import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { localizeMessage } from '../utils/toast.js';

const GOOGLE_SCRIPT_ID = "greenwich-google-oauth";
const FACEBOOK_SCRIPT_ID = "greenwich-facebook-sdk";
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
const FACEBOOK_APP_ID = process.env.REACT_APP_FACEBOOK_APP_ID || "";

const ensureScript = (id, src) => new Promise((resolve, reject) => {
  const existing = document.getElementById(id);
  if (existing) {
    if (existing.dataset.loaded === "true") {
      resolve();
      return;
    }
    existing.addEventListener("load", resolve, { once: true });
    existing.addEventListener("error", reject, { once: true });
    return;
  }

  const script = document.createElement("script");
  script.id = id;
  script.src = src;
  script.async = true;
  script.defer = true;
  script.onload = () => {
    script.dataset.loaded = "true";
    resolve();
  };
  script.onerror = reject;
  document.head.appendChild(script);
});

function SocialButton({ disabled, onClick, children, style }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        border: "1px solid #d7e4dc",
        background: disabled ? "#f3f5f4" : "#ffffff",
        color: disabled ? "#8aa194" : "#173025",
        borderRadius: 12,
        padding: "11px 14px",
        fontSize: 14,
        fontWeight: 700,
        cursor: disabled ? "not-allowed" : "pointer",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 10,
        ...style,
      }}
    >
      {children}
    </button>
  );
}

export default function SocialAuthButtons({
  onAuth,
  disabled = false,
  compact = false,
  showHint = true,
  mode = "continue",
}) {
  const { t } = useTranslation();
  const [sdkReady, setSdkReady] = useState({ google: false, facebook: false });
  const [loadingProvider, setLoadingProvider] = useState("");
  const [error, setError] = useState("");

  const configState = useMemo(() => ({
    google: Boolean(GOOGLE_CLIENT_ID),
    facebook: Boolean(FACEBOOK_APP_ID),
  }), []);

  useEffect(() => {
    let cancelled = false;

    const loadGoogle = async () => {
      if (!configState.google) return;
      try {
        await ensureScript(GOOGLE_SCRIPT_ID, "https://accounts.google.com/gsi/client");
        if (!cancelled) {
          setSdkReady((prev) => ({ ...prev, google: Boolean(window.google?.accounts?.oauth2) }));
        }
      } catch (err) {
        if (!cancelled) {
          setError(t('socialAuth.googleLoadFailed'));
        }
      }
    };

    const loadFacebook = async () => {
      if (!configState.facebook) return;
      try {
        await ensureScript(FACEBOOK_SCRIPT_ID, "https://connect.facebook.net/en_US/sdk.js");
        if (window.FB?.init) {
          window.FB.init({
            appId: FACEBOOK_APP_ID,
            cookie: true,
            xfbml: false,
            version: "v19.0",
          });
        }
        if (!cancelled) {
          setSdkReady((prev) => ({ ...prev, facebook: Boolean(window.FB?.login) }));
        }
      } catch (err) {
        if (!cancelled) {
          setError(t('socialAuth.facebookLoadFailed'));
        }
      }
    };

    loadGoogle();
    loadFacebook();

    return () => {
      cancelled = true;
    };
  }, [configState.facebook, configState.google]);

  const handleGoogle = async () => {
    if (!window.google?.accounts?.oauth2 || disabled || loadingProvider) return;
    setError("");
    setLoadingProvider("google");

    try {
      await new Promise((resolve, reject) => {
        const client = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: "openid email profile",
          callback: (response) => {
            if (response?.access_token) {
              resolve(response.access_token);
            } else {
              reject(new Error(t('socialAuth.googleCancelled')));
            }
          },
          error_callback: () => reject(new Error(t('socialAuth.googleFailed'))),
        });
        client.requestAccessToken({ prompt: "consent" });
      }).then((accessToken) => onAuth({ provider: "google", accessToken }));
    } catch (err) {
      setError(err.message || t('socialAuth.googleFailed'));
    } finally {
      setLoadingProvider("");
    }
  };

  const handleFacebook = async () => {
    if (!window.FB?.login || disabled || loadingProvider) return;
    setError("");
    setLoadingProvider("facebook");

    try {
      await new Promise((resolve, reject) => {
        window.FB.login((response) => {
          const token = response?.authResponse?.accessToken;
          if (token) {
            resolve(token);
            return;
          }
          reject(new Error(t('socialAuth.facebookCancelled')));
        }, { scope: "public_profile,email" });
      }).then((accessToken) => onAuth({ provider: "facebook", accessToken }));
    } catch (err) {
      setError(err.message || t('socialAuth.facebookFailed'));
    } finally {
      setLoadingProvider("");
    }
  };

  const buttonsDisabled = disabled || loadingProvider !== "";
  const actionLabel = mode === "register" ? t('socialAuth.signUpWith') : t('socialAuth.continueWith');

  return (
    <div style={{ display: "grid", gap: compact ? 8 : 10 }}>
      <div style={{ display: "grid", gap: 8 }}>
        <SocialButton
          disabled={buttonsDisabled || !configState.google || !sdkReady.google}
          onClick={handleGoogle}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>G</span>
          {loadingProvider === "google" ? t('socialAuth.connectingGoogle') : `${actionLabel} Google`}
        </SocialButton>

        <SocialButton
          disabled={buttonsDisabled || !configState.facebook || !sdkReady.facebook}
          onClick={handleFacebook}
          style={{ color: buttonsDisabled ? "#8aa194" : "#1d4ed8" }}
        >
          <span style={{ fontSize: 18, lineHeight: 1 }}>f</span>
          {loadingProvider === "facebook" ? t('socialAuth.connectingFacebook') : `${actionLabel} Facebook`}
        </SocialButton>
      </div>

      {/* Dev-only diagnostic: never show raw env-var names to real users in production */}
      {showHint && process.env.NODE_ENV !== 'production' && (!configState.google || !configState.facebook) && (
        <div style={{ fontSize: 11, color: "#7b8d83", lineHeight: 1.5 }}>
          {t('socialAuth.configHint')}
        </div>
      )}

      {error && (
        <div style={{ fontSize: 12, color: "#b42318", background: "#fef3f2", border: "1px solid #fecdca", borderRadius: 10, padding: "9px 12px" }}>
          {localizeMessage(error)}
        </div>
      )}
    </div>
  );
}
